import type { AnyRouter } from '@tanstack/react-router';
import type { AppRouteMeta } from '@/routing/route-meta';
import { resolveNavigationTarget } from './navigationTargets';
import { acceptedSearchParams, type RouteForSearch } from './routeSearchParams';
import { getFlatRoutes } from '@/routing/flat-routes';

export type RouteValidation =
  | {
      ok: true;
      href: string;
      pathname: string;
      /** The query params this page accepts. Absent when they can't be read. */
      acceptedQueryParams?: string[];
    }
  | { ok: false; error: string };

// Only used to parse an in-app href; never navigated to.
const PARSE_ORIGIN = 'http://app.invalid';

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

/** Record pages one level below `pathname` (`/workbooks` → `/workbooks/$workbookId`). */
function recordPagesUnder(router: AnyRouter, pathname: string): string[] {
  const prefix = `${pathname.replace(/\/+$/, '')}/$`;
  return getFlatRoutes(router)
    .map((route) => route.fullPath)
    .filter(
      (path): path is string =>
        typeof path === 'string' &&
        path.startsWith(prefix) &&
        !path.slice(prefix.length).includes('/'),
    );
}

/**
 * Checks an in-app URL the way TanStack Router would take it, before the user
 * is sent there: the path must be a route this user can reach, every path
 * param must pass the route's `params.parse`, the query must pass each
 * matched route's `validateSearch`, and every query param must be one the
 * page accepts. That last check is ours: a Zod search schema silently drops
 * keys it doesn't know (`/workbooks?workbookId=…` just opens the list), so
 * without it a wrong param looks like a successful navigation.
 */
export function validateRoute(
  router: AnyRouter,
  href: string,
  canAccess: (meta: AppRouteMeta) => boolean,
): RouteValidation {
  const target = resolveNavigationTarget(router, href, canAccess);
  if (!target.ok) return target;

  const url = new URL(target.href, PARSE_ORIGIN);
  const search: Record<string, unknown> = router.options.parseSearch(
    url.search,
  );
  // Runs each matched route's params.parse, validateSearch and loaderDeps,
  // but not its context functions, and writes nothing to the match cache.
  const matches = router.matchRoutes(target.pathname, search);
  const invalid = routerValidationError(matches, target.pathname);
  if (invalid) return { ok: false, error: invalid };

  const leafId = matches[matches.length - 1]?.routeId;
  const leaf: RouteForSearch | undefined =
    leafId === undefined ? undefined : router.routesById[leafId];
  const accepted = leaf ? acceptedSearchParams(leaf) : null;
  const acceptedKeys = new Set(accepted);
  const unknown = accepted
    ? Object.keys(search).filter((key) => !acceptedKeys.has(key))
    : [];
  if (accepted && unknown.length > 0) {
    return {
      ok: false,
      error: unknownQueryError(router, target.pathname, unknown, accepted),
    };
  }
  return {
    ok: true,
    href: target.href,
    pathname: target.pathname,
    ...(accepted !== null && { acceptedQueryParams: accepted }),
  };
}

/** The first path-param or search validation error TanStack reported. */
function routerValidationError(
  matches: readonly { paramsError?: unknown; searchError?: unknown }[],
  pathname: string,
): string | null {
  for (const match of matches) {
    if (match.paramsError) {
      return `${pathname} has an invalid path param: ${errorMessage(match.paramsError)}`;
    }
    if (match.searchError) {
      return `${pathname} has an invalid query param: ${errorMessage(match.searchError)}`;
    }
  }
  return null;
}

/** Why the query params were refused, and where a record id belongs instead. */
function unknownQueryError(
  router: AnyRouter,
  pathname: string,
  unknown: string[],
  accepted: string[],
): string {
  const records = recordPagesUnder(router, pathname);
  const plural = unknown.length > 1 ? 's' : '';
  const takes =
    accepted.length > 0
      ? `it accepts ${accepted.join(', ')}.`
      : 'it accepts none.';
  const hint =
    records.length > 0
      ? ` To open one record, put its id in the path: ${records.join(', ')}.`
      : '';
  return `${pathname} does not take the query param${plural} ${unknown.join(', ')}; ${takes}${hint}`;
}
